<template>
  <div id="apiKeys">
    <h5>{{ language(1) }}</h5>
    <p class="text-muted small">{{ language(2) }}
      <a href="https://github.com/Orbitist/trail-hub/blob/main/docs/API.md" target="_blank">{{ language(3) }}</a>
    </p>

    <!-- Newly created key: shown exactly once -->
    <b-alert v-if="newKey" show variant="success">
      <strong>{{ language(4) }}</strong>
      <p class="small mb-2">{{ language(5) }}</p>
      <b-input-group>
        <b-form-input :value="newKey.key" readonly class="text-monospace"></b-form-input>
        <b-input-group-append>
          <b-btn variant="primary" v-clipboard:copy="newKey.key" v-clipboard:success="copied">
            <i class="far fa-copy"></i> {{ copyLabel }}
          </b-btn>
        </b-input-group-append>
      </b-input-group>
      <b-btn size="sm" variant="link" class="mt-2 p-0" @click="newKey = false">{{ language(6) }}</b-btn>
    </b-alert>

    <!-- Create form -->
    <b-form @submit.prevent="createKey" class="mb-4">
      <label for="apiKeyName">{{ language(7) }}</label>
      <b-form-input
        id="apiKeyName"
        v-model="form.name"
        :placeholder="language(8)"
        maxlength="100"
        class="mb-2"
        required
      ></b-form-input>
      <b-form-group :label="language(9)">
        <b-form-radio-group v-model="form.access" :options="accessOptions"></b-form-radio-group>
      </b-form-group>
      <b-form-group :label="language(10)" :description="language(11)">
        <b-form-select v-model="form.trailSystemId" :options="trailSystemOptions"></b-form-select>
      </b-form-group>
      <b-form-group :label="language(12)">
        <b-form-select v-model="form.expiresInDays" :options="expiryOptions"></b-form-select>
      </b-form-group>
      <b-btn type="submit" variant="primary" :disabled="creating || !form.name">
        <i class="fas fa-key"></i> {{ creating ? language(13) : language(14) }}
      </b-btn>
    </b-form>

    <!-- Existing keys -->
    <h6>{{ language(15) }}</h6>
    <p v-if="loading" class="text-muted small">{{ language(16) }}</p>
    <p v-else-if="activeKeys.length === 0" class="text-muted small">{{ language(17) }}</p>
    <b-table-simple v-else small responsive class="mb-3">
      <b-thead>
        <b-tr>
          <b-th>{{ language(7) }}</b-th>
          <b-th>{{ language(18) }}</b-th>
          <b-th>{{ language(9) }}</b-th>
          <b-th>{{ language(19) }}</b-th>
          <b-th></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="key in activeKeys" :key="key.id">
          <b-td>
            {{ key.name }}
            <div v-if="key.trailSystemIds.length" class="small text-muted">{{ trailSystemNames(key.trailSystemIds) }}</div>
            <div v-if="key.expiresAt" class="small text-muted">{{ language(20) }} {{ shortDate(key.expiresAt) }}</div>
          </b-td>
          <b-td class="text-monospace">{{ key.prefix }}…</b-td>
          <b-td>{{ key.scopes.includes('write') ? language(21) : language(22) }}</b-td>
          <b-td>{{ key.lastUsedOn ? shortDate(key.lastUsedOn) : language(23) }}</b-td>
          <b-td class="text-right">
            <b-btn size="sm" variant="outline-danger" @click="confirmRevoke = key">{{ language(24) }}</b-btn>
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>

    <b-alert v-if="error" show variant="warning" dismissible @dismissed="error = ''">{{ error }}</b-alert>

    <Confirm
      v-if="confirmRevoke"
      :text="language(25) + ' \'' + confirmRevoke.name + '\'? ' + language(26)"
      v-on:confirmed="revokeKey($event)"
    />
  </div>
</template>

<script>
import axios from 'axios'
import firebase from 'firebase'
import Confirm from '../Confirm'

// The API lives behind Firebase Hosting (/api/** -> functions). When the UI is
// served from localhost there is no rewrite, so fall back to production.
const apiBase = process.env.VUE_APP_API_BASE ||
  (window.location.hostname === 'localhost' ? 'https://trailhub.org' : '')

export default {
  components: { Confirm },
  data () {
    return {
      keys: [],
      loading: true,
      creating: false,
      newKey: false,
      copyLabel: '',
      error: '',
      confirmRevoke: false,
      form: {
        name: '',
        access: 'write',
        trailSystemId: '',
        expiresInDays: ''
      },
      phrases: {
        1: { en: 'API Keys', fr: 'Clés API' },
        2: { en: 'API keys let scripts and AI assistants manage your trail systems on your behalf. Treat them like passwords.', fr: 'Les clés API permettent aux scripts et aux assistants IA de gérer vos réseaux de sentiers en votre nom. Traitez-les comme des mots de passe.' },
        3: { en: 'API documentation', fr: 'Documentation de l’API' },
        4: { en: 'Your new API key', fr: 'Votre nouvelle clé API' },
        5: { en: 'Copy it now. For your security it will not be shown again.', fr: 'Copiez-la maintenant. Pour votre sécurité, elle ne sera plus affichée.' },
        6: { en: 'I have saved my key', fr: 'J’ai enregistré ma clé' },
        7: { en: 'Name', fr: 'Nom' },
        8: { en: 'e.g. Claude assistant, Grooming script', fr: 'p. ex. Assistant Claude, Script de damage' },
        9: { en: 'Access', fr: 'Accès' },
        10: { en: 'Limit to one trail system', fr: 'Limiter à un réseau de sentiers' },
        11: { en: 'Optional. By default the key can access every trail system you manage.', fr: 'Facultatif. Par défaut, la clé donne accès à tous les réseaux que vous gérez.' },
        12: { en: 'Expires', fr: 'Expire' },
        13: { en: 'Creating…', fr: 'Création…' },
        14: { en: 'Create API key', fr: 'Créer une clé API' },
        15: { en: 'Active keys', fr: 'Clés actives' },
        16: { en: 'Loading…', fr: 'Chargement…' },
        17: { en: 'You have no API keys yet.', fr: 'Vous n’avez pas encore de clé API.' },
        18: { en: 'Key', fr: 'Clé' },
        19: { en: 'Last used', fr: 'Dernière utilisation' },
        20: { en: 'Expires', fr: 'Expire le' },
        21: { en: 'Read & write', fr: 'Lecture et écriture' },
        22: { en: 'Read only', fr: 'Lecture seule' },
        23: { en: 'Never', fr: 'Jamais' },
        24: { en: 'Revoke', fr: 'Révoquer' },
        25: { en: 'Revoke the key', fr: 'Révoquer la clé' },
        26: { en: 'Anything using it will stop working immediately.', fr: 'Tout ce qui l’utilise cessera de fonctionner immédiatement.' },
        27: { en: 'Copy', fr: 'Copier' },
        28: { en: 'Copied!', fr: 'Copié !' },
        29: { en: 'All my trail systems', fr: 'Tous mes réseaux de sentiers' },
        30: { en: 'Never', fr: 'Jamais' },
        31: { en: 'days', fr: 'jours' },
        32: { en: 'year', fr: 'an' }
      }
    }
  },
  computed: {
    activeKeys () {
      return this.keys.filter(k => !k.revoked)
    },
    accessOptions () {
      return [
        { value: 'write', text: this.language(21) },
        { value: 'read', text: this.language(22) }
      ]
    },
    trailSystemOptions () {
      const options = [{ value: '', text: this.language(29) }]
      ;(this.$store.state.trailSystems || []).forEach(ts => {
        options.push({ value: ts.id, text: ts.name })
      })
      return options
    },
    expiryOptions () {
      return [
        { value: '', text: this.language(30) },
        { value: 30, text: '30 ' + this.language(31) },
        { value: 90, text: '90 ' + this.language(31) },
        { value: 365, text: '1 ' + this.language(32) }
      ]
    }
  },
  mounted () {
    this.copyLabel = this.language(27)
    this.fetchKeys()
  },
  methods: {
    language (id) {
      const lang = (this.$store.state.trailSystem && this.$store.state.trailSystem.language) || 'en'
      return this.phrases[id][lang] || this.phrases[id].en
    },
    async authHeaders () {
      const token = await firebase.auth().currentUser.getIdToken()
      return { headers: { Authorization: 'Bearer ' + token } }
    },
    apiError (err) {
      const data = err.response && err.response.data
      this.error = (data && data.error && data.error.message) || err.message
    },
    async fetchKeys () {
      this.loading = true
      try {
        const res = await axios.get(apiBase + '/api/v1/keys', await this.authHeaders())
        this.keys = res.data.keys
      } catch (err) {
        this.apiError(err)
      }
      this.loading = false
    },
    async createKey () {
      if (!this.form.name) return
      this.creating = true
      this.error = ''
      const body = {
        name: this.form.name,
        scopes: this.form.access === 'write' ? ['read', 'write'] : ['read']
      }
      if (this.form.trailSystemId) body.trailSystemIds = [this.form.trailSystemId]
      if (this.form.expiresInDays) body.expiresInDays = this.form.expiresInDays
      try {
        const res = await axios.post(apiBase + '/api/v1/keys', body, await this.authHeaders())
        this.newKey = res.data
        this.copyLabel = this.language(27)
        this.form.name = ''
        await this.fetchKeys()
      } catch (err) {
        this.apiError(err)
      }
      this.creating = false
    },
    async revokeKey (confirmed) {
      const key = this.confirmRevoke
      this.confirmRevoke = false
      if (!confirmed || !key) return
      try {
        await axios.delete(apiBase + '/api/v1/keys/' + key.id, await this.authHeaders())
        await this.fetchKeys()
      } catch (err) {
        this.apiError(err)
      }
    },
    copied () {
      this.copyLabel = this.language(28)
    },
    trailSystemNames (ids) {
      const systems = this.$store.state.trailSystems || []
      return ids.map(id => {
        const ts = systems.find(s => s.id === id)
        return ts ? ts.name : id
      }).join(', ')
    },
    shortDate (iso) {
      return new Date(iso).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.text-monospace {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
</style>
